import { notifyRequestFailure } from "@/lib/deployment/request-failures";
import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { useAuthStore } from "@/lib/store/auth";
import { authApi } from "./auth";
import { ApiError } from "@/types/Api";
import {
  isSessionRejection,
  SessionEndedError,
  SessionRefreshUnavailableError,
  toHttpError,
} from "./http-error";
import { API_BASE_PATH } from "./constants";

//TODO: add rate limiting when receiving 429
export function handleResponse(response: any): any {
  if (response.status === 204) {
    return {};
  }

  if (response.status === 200) {
    return response.data;
  }

  throw new Error("Invalid response format");
}

// Helper to detect invalid/expired token errors
// Returns true for: 401 OR (403 with INVALID_TOKEN code)
// MUST be defined before the generic error handler to check auth errors
function isInvalidTokenError(error: AxiosError<ApiError>): boolean {
  // Always logout on 401 Unauthorized
  if (error.response?.status === 401) return true;

  // Only logout on 403 if code is explicitly INVALID_TOKEN
  // Do NOT logout for 403 FORBIDDEN or other permission errors
  if (error.response?.status === 403) {
    const code = error.response.data?.errors?.[0]?.extensions?.code;
    return code === "INVALID_TOKEN";
  }

  return false;
}

// HttpError and its mapping live in ./http-error so a test can reach them
// without importing this module's cycle. Re-exported here: callers have always
// imported it from the client.
export { HttpError } from "./http-error";

// Base configuration
const baseConfig = {
  baseURL: API_BASE_PATH,
  timeout: 1200000,
  headers: {
    "Content-Type": "application/json",
  },
};

// Public client for non-authenticated requests
export const publicClient = axios.create({
  ...baseConfig,
  withCredentials: false, // No credentials for public endpoints
});

// Protected client for authenticated requests
export const apiClient = axios.create({
  ...baseConfig,
  withCredentials: true, // Include credentials for protected endpoints
});

// Global error handler for both clients
const handleAxiosError = async (error: AxiosError<ApiError>) => {
  if (!error.response) {
    // Network error or timeout
    throw new Error("Network error - please check your connection");
  }

  const { status, data } = error.response;

  throw toHttpError(status, data, error.config?.url);
};

// Shared request interceptor for both clients (query param transform +
// request-id header). The id is generated once per call so the browser's
// network tab, the app's own Sentry context, and the backend's logs share one
// key — @yp/api's `genRequestId` honors a sanitized inbound x-request-id and
// echoes it back, so a browser issue can be pivoted straight to API logs.
[apiClient, publicClient].forEach((client) => {
  client.interceptors.request.use((config) => {
    // Transform query parameters if they exist
    if (config.params) {
      // Convert fields array to comma-separated string
      if (Array.isArray(config.params.fields)) {
        config.params.fields = config.params.fields.join(",");
      }
    }
    // Keep within the API's sanitizer contract ([A-Za-z0-9._-], <= 128 chars).
    config.headers["x-request-id"] = crypto.randomUUID();
    return config;
  });
});

// Observe failed HTTP requests before response/auth adapters consume them.
[apiClient, publicClient].forEach((client) => {
  client.interceptors.response.use(undefined, (error: unknown) => {
    if (!axios.isCancel(error)) notifyRequestFailure();
    return Promise.reject(error);
  });
});

// Public client: ALL errors go through handleAxiosError (extracts API error messages)
publicClient.interceptors.response.use(
  (response) => handleResponse(response),
  (error: AxiosError<ApiError>) => {
    if (error.code === "ECONNABORTED") {
      throw new Error("Request timeout - please try again");
    }
    return handleAxiosError(error);
  },
);

// API client: auth errors (401, 403+INVALID_TOKEN) pass through to the auth-specific interceptor below
apiClient.interceptors.response.use(
  (response) => handleResponse(response),
  (error: AxiosError<ApiError>) => {
    // Raised by the request interceptor before anything was sent; it already
    // says what happened and must not be recast as a network error.
    if ((error as unknown) instanceof SessionRefreshUnavailableError) {
      return Promise.reject(error);
    }
    if (error.code === "ECONNABORTED") {
      throw new Error("Request timeout - please try again");
    }
    if (isInvalidTokenError(error)) {
      return Promise.reject(error);
    }
    return handleAxiosError(error);
  },
);

let isRefreshing = false;

interface TokenWaiter {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}
let refreshSubscribers: TokenWaiter[] = [];

const subscribeTokenRefresh = (waiter: TokenWaiter) => {
  refreshSubscribers.push(waiter);
};

// Every caller parked behind an in-flight refresh settles with its outcome:
// the new token, null when the session ended, or the failure.
const settleRefreshSubscribers = (settle: (waiter: TokenWaiter) => void) => {
  const waiters = refreshSubscribers;
  refreshSubscribers = [];
  waiters.forEach(settle);
};

// Clears the session, preserving the current path as a post-login callback
// unless we're already on an auth page or the root.
export const clearSessionCookies = () => {
  const { clearCookies } = useAuthStore.getState();
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : undefined;
  const shouldPreserveCallback =
    currentPath && !currentPath.startsWith("/auth") && currentPath !== "/";
  clearCookies(shouldPreserveCallback && currentPath ? currentPath : null);
};

/**
 * Returns a valid access token for the current session, refreshing it
 * (single-flight, shared across every HTTP client in this module graph)
 * when it is within 10s of expiry.
 *
 * Returns null — after clearing the session cookies — when the session is
 * over: no usable tokens, or the server rejected the refresh token. Callers
 * then send the request unauthenticated and the server answers for it.
 *
 * Rejects with SessionRefreshUnavailableError when the refresh failed for a
 * reason that says nothing about the session (server or network trouble). The
 * cookies stay, the request that needed the token is not sent, and the caller
 * can retry.
 */
export async function acquireAccessToken(): Promise<string | null> {
  const { setAccessToken, setRefreshToken } = useAuthStore.getState();
  const { accessToken, tokenExpires } = useAuthStore
    .getState()
    .getAccessToken();
  const refreshToken = useAuthStore.getState().getRefreshToken();

  const isTokenExpired =
    !tokenExpires || Date.now() + 10000 > Number(tokenExpires);

  // No tokens at all - clear session
  if (!accessToken && !refreshToken) {
    clearSessionCookies();
    return null;
  }

  // Valid access token - use it
  if (accessToken && !isTokenExpired) {
    return accessToken;
  }

  // Access token expired but no refresh token - clear session
  if (accessToken && isTokenExpired && !refreshToken) {
    clearSessionCookies();
    return null;
  }

  // If token is expired and we have a refresh token, try to refresh
  if (refreshToken) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const tokens = await authApi.refreshToken(refreshToken);
        setAccessToken(tokens.access_token, tokens.expires);
        setRefreshToken(tokens.refresh_token);
        settleRefreshSubscribers((waiter) =>
          waiter.resolve(tokens.access_token),
        );
        return tokens.access_token;
      } catch (error) {
        console.error("Refresh failed", error);
        if (isSessionRejection(error)) {
          // A stale/invalid refresh token is routine session expiry, not a
          // defect — leave a breadcrumb instead of a Sentry event so genuine
          // refresh failures (backend outage, network errors) aren't drowned
          // out.
          Sentry.addBreadcrumb({
            category: "auth",
            message: "Token refresh failed: session expired",
            level: "info",
          });
          clearSessionCookies();
          settleRefreshSubscribers((waiter) => waiter.resolve(null));
          return null;
        }
        // The server could not answer, which says nothing about whether the
        // refresh token is still good: keep the session.
        Sentry.captureException(error, {
          tags: { area: "auth", stage: "token-refresh" },
        });
        const unavailable = new SessionRefreshUnavailableError(error);
        settleRefreshSubscribers((waiter) => waiter.reject(unavailable));
        throw unavailable;
      } finally {
        isRefreshing = false;
      }
    }
    // Wait for the in-flight token refresh to complete
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh({ resolve, reject });
    });
  }

  return null;
}

apiClient.interceptors.request.use(
  async (config) => {
    const token = await acquireAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Auth-specific response interceptor for apiClient
// This runs AFTER the generic handler above, which passes auth errors through
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const { clearCookies } = useAuthStore.getState();

    // Handle invalid/expired tokens (401 OR 403 with INVALID_TOKEN)
    if (isInvalidTokenError(error)) {
      // Clear cookies and redirect immediately WITHOUT calling backend logout
      // (calling logout would trigger another 403, causing infinite loop)
      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : undefined;
      const shouldPreserveCallback =
        currentPath && !currentPath.startsWith("/auth") && currentPath !== "/";

      // Client context (client_id, user_role, etc.) will be preserved in Zustand localStorage
      // when shouldPreserveCallback is true, allowing client pages to restore state after login
      clearCookies(shouldPreserveCallback && currentPath ? currentPath : null);
      return Promise.reject(
        new SessionEndedError("Session expired - please login again"),
      );
    }

    return Promise.reject(error);
  },
);

// Type guard for API errors
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}
